import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search, ScanLine, Send, Pencil, Trash2, Camera, X, PackageCheck, CircleDashed, ExternalLink, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api, { API } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const money = (v) => `$${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const miles = (v) => `${Number(v || 0).toLocaleString('en-US')} mi`;
const dispCls = { 'Keep for Inventory': 'text-primary border-primary/60', Wholesale: 't-warn', Auction: 't-warn' };

const emptyForm = {
  cliente: '', vin: '', ano: '', marca: '', modelo: '', trim: '', carroceria: '', combustivel: '',
  km: '', cor: '', placa: '', condicao: 'Bom', observacoes: '',
  customerAsking: '', has_payoff: false, payoff: '', payoff_bank: '', payoff_financing: '', appraisedValue: '', disposition: 'Keep for Inventory',
};

const Field = ({ label, children, span = 1 }) => (
  <div className={span === 2 ? 'md:col-span-2' : ''}>
    <p className="ent-label mb-1">{label}</p>
    {children}
  </div>
);

const TradeIn = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [cfg, setCfg] = useState({ dispositions: [], conditions: [], can_send: false });
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('todos');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [decoding, setDecoding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/trade-ins'); setRows(data.trade_ins || []); setStats(data.stats || {}); } catch (e) {}
  }, []);
  useEffect(() => { load(); api.get('/trade-ins/config').then((r) => setCfg(r.data)).catch(() => {}); }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setJustSaved(false); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setJustSaved(false);
    setForm({ ...emptyForm, ...t, ano: t.ano || '', km: t.km || '', customerAsking: t.customerAsking || '', has_payoff: !!t.has_payoff, payoff: t.payoff || '', payoff_bank: t.payoff_bank || '', payoff_financing: t.payoff_financing || '', appraisedValue: t.appraisedValue || '' });
    setOpen(true);
  };

  const decodeVin = async () => {
    const vin = (form.vin || '').trim();
    if (vin.length < 11) { toast({ title: 'VIN inválido', description: 'Digite o VIN completo (17 caracteres)' }); return; }
    setDecoding(true);
    try {
      const { data } = await api.get(`/vin/decode/${vin}`);
      if (!data.marca && !data.modelo) { toast({ title: 'VIN não encontrado', description: 'Preencha os dados manualmente' }); return; }
      setForm((f) => ({ ...f, ano: data.ano || f.ano, marca: data.marca || f.marca, modelo: data.modelo || f.modelo,
        trim: data.trim || f.trim, carroceria: data.carroceria || f.carroceria, combustivel: data.combustivel || f.combustivel }));
      toast({ title: 'VIN decodificado', description: `${data.ano} ${data.marca} ${data.modelo}` });
    } catch (e) { toast({ title: 'Erro ao decodificar', description: e.response?.data?.detail || '' }); }
    finally { setDecoding(false); }
  };

  const save = async () => {
    if (!form.cliente.trim()) { toast({ title: 'Informe o cliente' }); return; }
    if (!form.marca.trim() || !form.modelo.trim()) { toast({ title: 'Informe marca e modelo', description: 'Use o VIN decoder ou digite manualmente' }); return; }
    setBusy(true);
    try {
      if (editing) { await api.put(`/trade-ins/${editing.id}`, form); toast({ title: 'Avaliação atualizada' }); }
      else { const { data } = await api.post('/trade-ins', form); setEditing(data); setJustSaved(true); toast({ title: 'Avaliação criada', description: 'Status: não enviado ao estoque' }); }
      await load();
      if (editing) setOpen(false);
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (file) => {
    if (!editing || !file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      await api.post(`/trade-ins/${editing.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { data } = await api.get('/trade-ins');
      setRows(data.trade_ins || []); setStats(data.stats || {});
      setEditing((data.trade_ins || []).find((r) => r.id === editing.id) || editing);
    } catch (e) { toast({ title: 'Erro ao enviar foto', description: e.response?.data?.detail || '' }); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const delPhoto = async (pid) => {
    try {
      await api.delete(`/trade-ins/${editing.id}/photos/${pid}`);
      setEditing({ ...editing, photos: (editing.photos || []).filter((p) => p.id !== pid) });
      load();
    } catch (e) {}
  };

  const remove = async (t) => {
    try { await api.delete(`/trade-ins/${t.id}`); toast({ title: 'Avaliação excluída' }); load(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const confirmSend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trade-ins/${sendTarget.id}/send-to-inventory`, {});
      toast({ title: 'Enviado ao estoque', description: `${data.vehicle.desc} · #${data.vehicle.placa} — preço de venda pendente` });
      setSendTarget(null);
      load();
    } catch (e) { toast({ title: 'Não foi possível enviar', description: e.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => rows.filter((t) => {
    if (tab === 'pendentes' && (t.sent_to_inventory || t.disposition !== 'Keep for Inventory')) return false;
    if (tab === 'estoque' && !t.sent_to_inventory) return false;
    const s = q.trim().toLowerCase();
    return !s || `${t.cliente} ${t.veiculo} ${t.vin} ${t.placa} ${t.disposition}`.toLowerCase().includes(s);
  }), [rows, q, tab]);

  return (
    <div className="space-y-4" data-testid="trade-in-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-black uppercase tracking-tight">Trade-In</h1>
          <p className="text-[12px] text-muted-foreground">Avaliações de troca — o veículo só entra no estoque quando você enviar</p>
        </div>
        <button data-testid="ti-new" onClick={openNew} className="ent-btn ent-btn-primary h-9 px-4"><Plus className="w-3.5 h-3.5" /> Nova Avaliação</button>
      </div>

      <div className="ent-panel ent-ribbon" data-testid="ti-stats">
        <div className="px-4 py-3"><p className="ent-label">Avaliações</p><p className="dt-timer mt-1 text-2xl font-black leading-none">{stats.total ?? 0}</p><p className="mt-1 text-[11px] text-muted-foreground/70">total registrado</p></div>
        <div className="px-4 py-3"><p className="ent-label">Aguardando envio</p><p className={`dt-timer mt-1 text-2xl font-black leading-none ${stats.pending_inventory ? 't-warn' : ''}`} data-testid="ti-stat-pending">{stats.pending_inventory ?? 0}</p><p className="mt-1 text-[11px] text-muted-foreground/70">Keep for Inventory</p></div>
        <div className="px-4 py-3"><p className="ent-label">No estoque</p><p className="dt-timer mt-1 text-2xl font-black leading-none t-ok">{stats.sent ?? 0}</p><p className="mt-1 text-[11px] text-muted-foreground/70">já enviados</p></div>
        <div className="px-4 py-3"><p className="ent-label">Wholesale / Auction</p><p className="dt-timer mt-1 text-2xl font-black leading-none">{stats.wholesale ?? 0}</p><p className="mt-1 text-[11px] text-muted-foreground/70">fora do varejo</p></div>
        <div className="px-4 py-3"><p className="ent-label">Valor avaliado</p><p className="dt-timer mt-1 text-2xl font-black leading-none">{money(stats.appraised_total)}</p><p className="mt-1 text-[11px] text-muted-foreground/70">{money(stats.pending_value)} pendente</p></div>
      </div>

      <div className="ent-panel">
        <div className="ent-panel-head flex-wrap gap-2">
          {[['todos', 'Todas'], ['pendentes', 'Aguardando estoque'], ['estoque', 'No estoque']].map(([k, l]) => (
            <button key={k} data-testid={`ti-tab-${k}`} onClick={() => setTab(k)} className={`ent-btn ent-btn-sm ${tab === k ? 'text-primary border-primary/60' : ''}`}>{l}</button>
          ))}
          <div className="relative ml-auto">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input data-testid="ti-search" className="ent-input" style={{ width: 230, paddingLeft: 24 }} placeholder="Cliente, veículo, VIN…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
        {!filtered.length ? (
          <p className="ent-empty">Nenhuma avaliação encontrada</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="ent-table">
              <thead><tr><th>Cliente</th><th>Veículo</th><th className="hidden xl:table-cell">VIN</th><th className="text-right">Miles</th><th className="text-right">Avaliado</th><th>Destino</th><th>Estoque</th><th className="text-right">Ações</th></tr></thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} data-testid={`ti-row-${t.id}`}>
                    <td className="font-semibold">{t.cliente}</td>
                    <td><p className="font-semibold">{t.veiculo}</p><p className="text-[10.5px] t-label mono">{[t.trim, t.cor, t.condicao].filter(Boolean).join(' · ') || '—'}</p></td>
                    <td className="hidden xl:table-cell mono text-[11px] text-muted-foreground">{t.vin || '—'}</td>
                    <td className="text-right mono">{miles(t.km)}</td>
                    <td className="text-right mono font-bold">{money(t.appraisedValue)}</td>
                    <td><span className={`ent-tag ${dispCls[t.disposition] || ''}`}>{t.disposition}</span></td>
                    <td>
                      {t.sent_to_inventory ? (
                        <button data-testid={`ti-stock-${t.id}`} onClick={() => navigate(`/app/estoque?v=${t.vehicle_id}`)} className="ent-tag t-ok inline-flex items-center gap-1 hover:underline">
                          <PackageCheck className="w-3 h-3" /> No estoque · #{t.stock} <ExternalLink className="w-2.5 h-2.5" />
                        </button>
                      ) : (
                        <span className="ent-tag t-warn inline-flex items-center gap-1" data-testid={`ti-notsent-${t.id}`}><CircleDashed className="w-3 h-3" /> Não enviado ao estoque</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1.5">
                        {!t.sent_to_inventory && cfg.can_send && (
                          <button data-testid={`ti-send-${t.id}`} onClick={() => setSendTarget(t)} className="ent-btn ent-btn-sm ent-btn-primary"><Send className="w-3 h-3" /> Enviar ao estoque</button>
                        )}
                        <button data-testid={`ti-edit-${t.id}`} onClick={() => openEdit(t)} className="ent-btn ent-btn-icon border-0"><Pencil className="w-3.5 h-3.5" /></button>
                        {!t.sent_to_inventory && (
                          <button data-testid={`ti-del-${t.id}`} onClick={() => remove(t)} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-card border-border" data-testid="ti-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">{editing ? 'Editar avaliação' : 'Nova avaliação de trade-in'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 max-h-[62vh] overflow-y-auto pr-1">
            {justSaved && (
              <p className="md:col-span-4 border-l-2 border-[hsl(var(--ok))] bg-[hsl(var(--ok))]/5 px-3 py-2 text-[12px] t-ok" data-testid="ti-saved-hint">
                Avaliação salva com status "Não enviado ao estoque" — anexe fotos abaixo ou feche e use "Enviar ao estoque" quando quiser.
              </p>
            )}
            <Field label="Cliente" span={2}><input data-testid="ti-f-cliente" className="ent-input" value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} /></Field>
            <Field label="VIN" span={2}>
              <div className="flex gap-1.5">
                <input data-testid="ti-f-vin" className="ent-input mono" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} placeholder="17 caracteres" />
                <button data-testid="ti-decode" onClick={decodeVin} disabled={decoding} className="ent-btn ent-btn-sm whitespace-nowrap">
                  {decoding ? <Loader2 className="w-3 h-3 animate-spin" /> : <ScanLine className="w-3 h-3" />} Decodificar
                </button>
              </div>
            </Field>
            <Field label="Ano"><input data-testid="ti-f-ano" className="ent-input" value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value })} /></Field>
            <Field label="Marca"><input data-testid="ti-f-marca" className="ent-input" value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} /></Field>
            <Field label="Modelo"><input data-testid="ti-f-modelo" className="ent-input" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} /></Field>
            <Field label="Trim"><input data-testid="ti-f-trim" className="ent-input" value={form.trim} onChange={(e) => setForm({ ...form, trim: e.target.value })} /></Field>
            <Field label="Carroceria"><input data-testid="ti-f-carroceria" className="ent-input" value={form.carroceria} onChange={(e) => setForm({ ...form, carroceria: e.target.value })} /></Field>
            <Field label="Combustível"><input data-testid="ti-f-combustivel" className="ent-input" value={form.combustivel} onChange={(e) => setForm({ ...form, combustivel: e.target.value })} /></Field>
            <Field label="Miles"><input data-testid="ti-f-km" type="number" className="ent-input" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} /></Field>
            <Field label="Cor"><input data-testid="ti-f-cor" className="ent-input" value={form.cor} onChange={(e) => setForm({ ...form, cor: e.target.value })} /></Field>
            <Field label="Placa / Stock"><input data-testid="ti-f-placa" className="ent-input" value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value })} /></Field>
            <Field label="Condição">
              <select data-testid="ti-f-condicao" className="ent-input" value={form.condicao} onChange={(e) => setForm({ ...form, condicao: e.target.value })}>
                {(cfg.conditions || []).map((c) => <option key={c} value={c} className="bg-popover text-foreground">{c}</option>)}
              </select>
            </Field>
            <Field label="Destino">
              <select data-testid="ti-f-disposition" className="ent-input" value={form.disposition} onChange={(e) => setForm({ ...form, disposition: e.target.value })}>
                {(cfg.dispositions || []).map((d) => <option key={d} value={d} className="bg-popover text-foreground">{d}</option>)}
              </select>
            </Field>
            <Field label="Valor pedido"><input data-testid="ti-f-asking" type="number" className="ent-input" value={form.customerAsking} onChange={(e) => setForm({ ...form, customerAsking: e.target.value })} /></Field>
            <Field label="Valor avaliado"><input data-testid="ti-f-appraised" type="number" className="ent-input" value={form.appraisedValue} onChange={(e) => setForm({ ...form, appraisedValue: e.target.value })} /></Field>

            <div className="md:col-span-4 rounded-md border border-border bg-muted/20 px-3 py-2.5">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <button type="button" role="switch" aria-checked={!!form.has_payoff} data-testid="ti-f-has-payoff"
                  onClick={() => setForm({ ...form, has_payoff: !form.has_payoff, ...(form.has_payoff ? { payoff: '', payoff_bank: '', payoff_financing: '' } : {}) })}
                  className={`relative w-10 h-5 rounded-full transition-colors ${form.has_payoff ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${form.has_payoff ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
                <span className="text-[13px] font-semibold">Veículo tem financiamento em aberto (payoff)?</span>
              </label>
              {form.has_payoff && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3" data-testid="ti-payoff-fields">
                  <Field label="Valor do payoff *"><input data-testid="ti-f-payoff" type="number" className="ent-input" value={form.payoff} onChange={(e) => setForm({ ...form, payoff: e.target.value })} /></Field>
                  <Field label="Banco / credor *"><input data-testid="ti-f-payoff-bank" className="ent-input" value={form.payoff_bank} onChange={(e) => setForm({ ...form, payoff_bank: e.target.value })} placeholder="Ex: Capital One" /></Field>
                  <Field label="Financiamento / contrato"><input data-testid="ti-f-payoff-financing" className="ent-input" value={form.payoff_financing} onChange={(e) => setForm({ ...form, payoff_financing: e.target.value })} placeholder="Nº do contrato" /></Field>
                </div>
              )}
              <p className="text-[11px] t-label mt-2">Ao salvar com payoff ativo, o registro aparece em Financeiro › Payoffs para o pagamento ao banco.</p>
            </div>
            <Field label="Observações / condição" span={2}><input data-testid="ti-f-obs" className="ent-input" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></Field>

            <div className="md:col-span-4 border-t border-border pt-3">
              <div className="flex items-center gap-2">
                <p className="ent-label">Fotos {editing ? `(${(editing.photos || []).length})` : ''}</p>
                {editing ? (
                  <>
                    <input ref={fileRef} data-testid="ti-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                    <button data-testid="ti-photo-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="ent-btn ent-btn-sm"><Camera className="w-3 h-3" /> Adicionar foto</button>
                  </>
                ) : <span className="text-[11px] text-muted-foreground">Salve a avaliação para anexar fotos</span>}
              </div>
              {!!editing?.photos?.length && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {editing.photos.map((p) => (
                    <div key={p.id} className="relative w-24 h-16 border border-border overflow-hidden group">
                      <img src={`${API}/files/${p.path}`} alt="" className="w-full h-full object-cover" />
                      <button data-testid={`ti-photo-del-${p.id}`} onClick={() => delPhoto(p.id)} className="absolute top-0 right-0 bg-background/80 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="ent-btn h-9 px-4">Fechar</button>
            <button data-testid="ti-save" disabled={busy} onClick={save} className="ent-btn ent-btn-primary h-9 px-5">{editing ? 'Salvar alterações' : 'Criar avaliação'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sendTarget} onOpenChange={(o) => !o && setSendTarget(null)}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="ti-send-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Enviar ao estoque</DialogTitle></DialogHeader>
          {sendTarget && (
            <div className="space-y-2 text-[12.5px]">
              <p className="font-semibold text-foreground">{sendTarget.veiculo}</p>
              <p className="t-label mono text-[11px]">{sendTarget.vin || 'sem VIN'} · {miles(sendTarget.km)}</p>
              <div className="border border-border divide-y divide-border mt-2">
                <div className="flex justify-between px-3 py-2"><span className="ent-label">Custo (valor avaliado)</span><span className="mono font-bold">{money(sendTarget.appraisedValue)}</span></div>
                <div className="flex justify-between px-3 py-2"><span className="ent-label">Preço de venda</span><span className="t-warn">a definir no Estoque</span></div>
                <div className="flex justify-between px-3 py-2"><span className="ent-label">Inspeção</span><span>criada automaticamente</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setSendTarget(null)} className="ent-btn h-9 px-4">Cancelar</button>
            <button data-testid="ti-send-confirm" disabled={busy} onClick={confirmSend} className="ent-btn ent-btn-primary h-9 px-5"><Send className="w-3.5 h-3.5" /> Confirmar envio</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TradeIn;
